import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import pb from "../../services/pocketbase";
import { validationPopup } from "../../redux/actions";
import { EVENT_REGISTRATION_EVENT_SLUG, JOB_TITLE_OPTIONS } from "./eventRegistrationConfig";

const initialForm = () => ({
  name: "",
  email: "",
  company: "",
  job_title: "",
  radio_choice: "",
  check_topics: [],
  qa_prefill: "",
  referral_source: "",
  event_materials_ok: false,
  notices_ok: false,
});

/**
 * Same behavior pattern as ContactC: form + touched + useMemo errors,
 * markAllTouched on submit, PocketBase create, thanks mode + 7s reset.
 */
export function useEventRegistrationForm() {
  const dispatch = useDispatch();
  const [form, setForm] = useState(initialForm);
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [mode, setMode] = useState("form");
  const resetTimerRef = useRef(null);

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const toggleCheckbox = (value) => {
    setForm((prev) => {
      const set = new Set(prev.check_topics || []);
      if (set.has(value)) set.delete(value);
      else set.add(value);
      return { ...prev, check_topics: Array.from(set) };
    });
  };

  const errors = useMemo(() => {
    const next = {};
    const name = String(form.name || "").trim();
    const email = String(form.email || "").trim();
    const jobTitle = String(form.job_title || "").trim();

    if (!name) next.name = "お名前を入力してください。";
    if (!email) next.email = "メールアドレスを入力してください。";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      next.email = "有効なメールアドレスを入力してください。";
    if (!jobTitle) next.job_title = "職種 / 役割を選択してください。";
    else if (!JOB_TITLE_OPTIONS.includes(jobTitle)) next.job_title = "職種 / 役割を選択してください。";

    if (!form.notices_ok) next.notices_ok = "プライバシーポリシーへの同意が必要です。";

    const qa = String(form.qa_prefill || "").trim();
    if (qa.length > 800) next.qa_prefill = "800文字以内で入力してください。";

    return next;
  }, [form]);

  const isValid = Object.keys(errors).length === 0;

  useEffect(() => {
    return () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    };
  }, []);

  const showThanksThenReset = () => {
    setMode("thanks");
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(() => {
      setMode("form");
      setSubmitting(false);
      setSubmitError("");
      setTouched({});
      setForm(initialForm());
    }, 7000);
  };

  const onAnyChange = (target) => {
    if (target === "ALL") return;
    const name = target?.name;
    if (!name) return;
    const raw = target?.value;
    const value = typeof raw === "boolean" ? raw : raw ?? "";
    setField(name, value);
  };

  const onBlur = (e) => {
    const name = e?.target?.name;
    if (!name) return;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const touchField = (fieldName) => {
    if (!fieldName) return;
    setTouched((prev) => ({ ...prev, [fieldName]: true }));
  };

  const markAllTouched = () => {
    setTouched({
      name: true,
      email: true,
      job_title: true,
      notices_ok: true,
      qa_prefill: true,
    });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setSubmitError("");
    markAllTouched();
    if (!isValid || submitting) {
      dispatch(
        validationPopup({
          message: "入力内容をご確認ください。",
          type: "error",
          icon: "info",
        })
      );
      return;
    }

    setSubmitting(true);
    const radio = String(form.radio_choice || "").trim();
    const checks = Array.isArray(form.check_topics) ? form.check_topics : [];
    const checkJoined = checks.length > 0 ? checks.join("|") : "未回答";
    const qa = String(form.qa_prefill || "").trim();
    const refSrc = String(form.referral_source || "").trim();
    const detailLines = [
      `UX/UIデザイン経験: ${radio || "未回答"}`,
      `参加目的: ${checks.length > 0 ? checks.join("、") : "未回答"}`,
      `Q&Aで聞いてみたいこと: ${qa || "（なし）"}`,
      `このイベントをどこで知りましたか: ${refSrc || "（未記入）"}`,
    ];
    const messageBody = detailLines.join("\n");
    try {
      await pb.collection("event_registrations").create({
        event_slug: EVENT_REGISTRATION_EVENT_SLUG,
        name: String(form.name || "").trim(),
        email: String(form.email || "").trim(),
        company: String(form.company || "").trim(),
        job_title: String(form.job_title || "").trim(),
        topic: "イベント参加",
        message: messageBody,
        radio_choice: radio || "未回答",
        check_topics: checkJoined,
        privacy_ok: !!form.notices_ok,
      });
      dispatch(
        validationPopup({
          message: "お申し込みを受け付けました。",
          type: "success",
          icon: "check",
        })
      );
      showThanksThenReset();
    } catch (err) {
      const msg =
        err?.data?.message ||
        err?.message ||
        "送信に失敗しました。しばらくしてから再度お試しください。";
      setSubmitError(String(msg));
      dispatch(
        validationPopup({
          message: "送信に失敗しました。しばらくしてから再度お試しください。",
          type: "error",
          icon: "warning",
        })
      );
      setSubmitting(false);
    }
  };

  return {
    form,
    setField,
    toggleCheckbox,
    errors,
    touched,
    submitting,
    submitError,
    mode,
    isValid,
    onAnyChange,
    onBlur,
    touchField,
    handleSubmit,
  };
}
